/**
 * Every identity endpoint, typed from the contracts rather than restated here.
 *
 * The methods are deliberately thin. Anything that looks like policy - when to
 * refresh, what a 401 means, which failure is worth retrying - belongs to
 * `ApiClient` or to the caller, so that adding an endpoint stays a one-line job
 * and no rule ends up implemented twice.
 *
 * `anonymous: true` marks the calls that must NOT carry an access token or
 * trigger the refresh-and-retry dance: they are the ones you make when you have
 * no session, and a 401 from them is the answer, not a signal to refresh.
 */
export function createIdentityApi(http) {
    return {
        /** POST /identity/auth/signup - always 202, whatever the address turns out to be. */
        signup: (body) => http.post('/identity/auth/signup', body, { anonymous: true }),
        /** POST /identity/auth/resend-verification - also always 202. */
        resendVerification: (body) => http.post('/identity/auth/resend-verification', body, { anonymous: true }),
        /** POST /identity/auth/verify-email - yields an enrolment ticket, not a session. */
        verifyEmail: (body) => http.post('/identity/auth/verify-email', body, { anonymous: true }),
        /** POST /identity/auth/totp/start - the secret to render as a QR code. */
        startTotpEnrolment: (body) => http.post('/identity/auth/totp/start', body, { anonymous: true }),
        /**
         * POST /identity/auth/totp/confirm - activates the account.
         *
         * The recovery codes in the response exist nowhere else, in any form: they
         * are stored hashed, so a UI that discards them has destroyed them.
         */
        confirmTotpEnrolment: (body) => http.post('/identity/auth/totp/confirm', body, { anonymous: true }),
        /** POST /identity/auth/login - first factor. Returns a challenge, never a session. */
        login: (body) => http.post('/identity/auth/login', body, { anonymous: true }),
        /** POST /identity/auth/login/totp - closes the challenge and opens the session. */
        completeLoginWithTotp: (body) => http.post('/identity/auth/login/totp', body, { anonymous: true }),
        /** POST /identity/auth/login/recovery - the same, for someone who lost their authenticator. */
        completeLoginWithRecoveryCode: (body) => http.post('/identity/auth/login/recovery', body, { anonymous: true }),
        /**
         * POST /identity/auth/logout.
         *
         * Not anonymous: the access token identifies the account, and for a native
         * client its `sid` claim is the only thing that says which device to end.
         */
        logout: (body = {}) => http.post('/identity/auth/logout', body),
        /** POST /identity/auth/password/forgot - always 202. */
        forgotPassword: (body) => http.post('/identity/auth/password/forgot', body, { anonymous: true }),
        /** POST /identity/auth/password/reset - consuming the link ends every session. */
        resetPassword: (body) => http.post('/identity/auth/password/reset', body, { anonymous: true }),
        /** POST /identity/auth/password/change - keeps this device, signs every other one out. */
        changePassword: (body) => http.post('/identity/auth/password/change', body),
        /** GET /identity/me - profile, plan, usage and app access in one call. */
        me: () => http.get('/identity/me'),
        /** POST /identity/me */
        updateMe: (body) => http.post('/identity/me', body),
        /** GET /identity/sessions - the "your devices" list, current one flagged. */
        listSessions: () => http.get('/identity/sessions'),
        /** DELETE /identity/sessions/:id - signs one device out. */
        revokeSession: (id) => http.delete(`/identity/sessions/${encodeURIComponent(id)}`),
        /**
         * POST /identity/me/deletion - schedules the erasure and ends every session,
         * this one included. The caller forgets the session afterwards; see
         * `AuthSession.deleteAccount`, which is what a screen should call.
         */
        requestAccountDeletion: (body) => http.post('/identity/me/deletion', body),
        /** POST /identity/auth/deletion/cancel - the link in the email. Anonymous: every session ended with the request. */
        cancelAccountDeletion: (body) => http.post('/identity/auth/deletion/cancel', body, { anonymous: true }),
        /** GET /identity/admin/users - cursor-paginated; pass `nextCursor` back to page on. */
        listUsers: (query = {}) => http.get('/identity/admin/users', { query }),
        /** POST /identity/admin/users/:id - needs a recently presented second factor. */
        updateUser: (id, body) => http.post(`/identity/admin/users/${encodeURIComponent(id)}`, body),
    };
}
//# sourceMappingURL=identity.js.map